import { useState } from 'react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { exportToCsv } from '@/lib/exportCsv';
import { exportTableToPDF } from '@/lib/pdfExport';
import { toast } from 'sonner';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COLS = [
  { key: 'supplier', label: 'Supplier' }, { key: 'gl_ap_balance', label: 'GL AP' }, { key: 'ledger_balance', label: 'Supplier ledger' },
  { key: 'difference', label: 'Difference' }, { key: 'bills_not_in_gl', label: 'Credit bills not in GL' },
  { key: 'payments_not_in_gl', label: 'Payments not in GL' }, { key: 'explanation', label: 'Explanation', format: (v) => (v || []).join('; ') },
];

// Read-only AP reconciliation: GL 021-1 by supplier vs supplier ledgers, with evidence columns.
export function ApReconcilePanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get('/accounting/ap-reconciliation'); setD(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'AP reconciliation failed'); }
    finally { setBusy(false); }
  };
  const pdf = () => exportTableToPDF('AP Reconciliation', `GL ${fmt(d.gl_ap_total)} vs ledgers ${fmt(d.ledger_total)}`,
    COLS.map((c) => c.label), d.suppliers.map((r) => COLS.map((c) => (c.format ? c.format(r[c.key]) : (typeof r[c.key] === 'number' ? r[c.key].toFixed(2) : r[c.key] ?? '')))), { landscape: true });
  return (
    <div className="rounded-2xl border border-border p-4 space-y-2" data-testid="ap-reconcile-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-bold">AP reconciliation by supplier (read-only)</div>
          <p className="text-[11px] text-muted-foreground">GL Accounts Payable per supplier vs supplier ledgers. Changes nothing.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={run} disabled={busy} data-testid="ap-reconcile-run-btn">{busy ? 'Checking…' : 'Run'}</Button>
        {d && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => exportToCsv(`ap_reconciliation_${new Date().toISOString().slice(0, 10)}.csv`, d.suppliers, COLS)} data-testid="ap-reconcile-csv-btn">Excel (CSV)</Button>}
        {d && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={pdf} data-testid="ap-reconcile-pdf-btn">PDF</Button>}
      </div>
      {d && (
        <>
          <div className="text-[11px] flex gap-4 flex-wrap" data-testid="ap-reconcile-summary">
            <span>GL AP: <b>{fmt(d.gl_ap_total)}</b></span>
            <span>Supplier ledgers: <b>{fmt(d.ledger_total)}</b></span>
            <span>Difference: <b className={Math.abs(d.difference) >= 0.01 ? 'text-rose-600' : 'text-emerald-600'}>{fmt(d.difference)}</b></span>
            <span>AP lines with no supplier source: {fmt(d.ap_without_supplier_source.balance)}</span>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded-md">
            <table className="w-full text-xs" data-testid="ap-reconcile-table">
              <thead className="bg-muted sticky top-0"><tr>{COLS.map((c) => <th key={c.key} className="text-left px-2 py-1">{c.label}</th>)}</tr></thead>
              <tbody>{d.suppliers.map((r) => (
                <tr key={r.supplier_id} className="border-t">
                  {COLS.map((c) => <td key={c.key} className="px-2 py-1 tabular-nums">{c.format ? c.format(r[c.key]) : typeof r[c.key] === 'number' ? fmt(r[c.key]) : r[c.key]}</td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
